import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ConfirmDialog } from "../components/admin/ConfirmDialog";
import TopBar from "../components/TopBar";
import CefrBadge from "../components/CefrBadge";
import B1Verdict from "../components/B1Verdict";
import { DecisionTag } from "../components/admin/DecisionPanel";
import { formatDateTime, formatDuration, isB1 } from "../lib/cefr";
import { AttemptFilters } from "../components/admin/AttemptFilters";
import { CheckCircle, Hourglass, ShieldCheck, UsersThree, ChartBar, Trophy, MagnifyingGlass, WhatsappLogo, Envelope, SpeakerHigh, Trash, DownloadSimple, Eye, Microphone } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { AudioManager } from "../components/admin/AudioManager";
import { SpeakingManager } from "../components/admin/SpeakingManager";

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [q, setQ] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [tab, setTab] = useState("users");
  const { user: me } = useAuth();
  const [selUsers, setSelUsers] = useState(new Set());
  const [selAttempts, setSelAttempts] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [b1Filter, setB1Filter] = useState("all");

  const load = useCallback(() => Promise.all([
    api.get("/admin/users"),
    api.get("/admin/attempts"),
  ]).then(([u, a]) => { setUsers(u.data); setAttempts(a.data); })
    .catch(() => { setUsers([]); setAttempts([]); }), []);

  useEffect(() => { load(); }, [load]);

  const toggle = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleUser = toggle(setSelUsers);
  const toggleAttempt = toggle(setSelAttempts);

  const askRole = (u) => {
    const promote = u.role !== "admin";
    setConfirm({
      title: promote ? "Make admin?" : "Remove admin access?",
      body: promote
        ? `${u.name || u.email} (${u.email}) will be able to see every applicant, change scores and decisions, and delete users and results.`
        : `${u.name || u.email} (${u.email}) will become a regular user and lose access to the admin area.`,
      confirmLabel: promote ? "Make admin" : "Remove admin",
      tone: promote ? "primary" : "danger",
      request: () => api.put(`/admin/users/${u.id}/role`, { role: promote ? "admin" : "user" }),
      successMsg: promote ? `${u.email} is now an admin` : `${u.email} is no longer an admin`,
    });
  };

  const runDelete = async () => {
    setBusy(true);
    try {
      const { data } = await confirm.request();
      if (confirm.successMsg) {
        toast.success(confirm.successMsg);
        setConfirm(null);
        await load();
        return;
      }
      const parts = [];
      if (data.deleted_users) parts.push(`${data.deleted_users} user${data.deleted_users > 1 ? "s" : ""}`);
      parts.push(`${data.deleted_attempts || 0} result${data.deleted_attempts === 1 ? "" : "s"}`);
      toast.success(`Deleted ${parts.join(" and ")}`);
      setSelUsers(new Set());
      setSelAttempts(new Set());
      if (selectedUser && confirm.userIds?.includes(selectedUser.id)) setSelectedUser(null);
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (confirm?.successMsg ? "Could not change admin access" : "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  const askDeleteUsers = (list) => setConfirm({
    title: list.length === 1 ? "Delete user?" : `Delete ${list.length} users?`,
    body: list.length === 1
      ? `${list[0].name || list[0].email} (${list[0].email}) and all ${list[0].attempts_count} of their test results will be permanently deleted.`
      : `${list.length} users and all ${list.reduce((n, u) => n + u.attempts_count, 0)} of their test results will be permanently deleted.`,
    userIds: list.map((u) => u.id),
    request: () => (list.length === 1
      ? api.delete(`/admin/users/${list[0].id}`)
      : api.post("/admin/users/bulk-delete", { ids: list.map((u) => u.id) })),
  });

  const askDeleteAttempts = (list) => setConfirm({
    title: list.length === 1 ? "Delete result?" : `Delete ${list.length} results?`,
    body: list.length === 1
      ? `The test result of ${list[0].user_name || list[0].user_email} (${Math.round(list[0].total_score)}/100, ${formatDateTime(list[0].created_at)}) will be permanently deleted.`
      : `${list.length} test results will be permanently deleted. The users' accounts are kept.`,
    request: () => (list.length === 1
      ? api.delete(`/admin/attempts/${list[0].attempt_id}`)
      : api.post("/admin/attempts/bulk-delete", { ids: list.map((a) => a.attempt_id) })),
  });

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `voxlingua-results-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!users) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(needle) ||
      (u.name || "").toLowerCase().includes(needle) ||
      (u.whatsapp_number || "").toLowerCase().includes(needle),
    );
  }, [users, q]);

  const selectableUsers = (filteredUsers || []).filter((u) => u.id !== me?.id);

  const userAttempts = useMemo(() => {
    if (!attempts) return null;
    if (!selectedUser) return attempts;
    return attempts.filter((a) => a.user_id === selectedUser.id);
  }, [attempts, selectedUser]);

  const filteredAttempts = useMemo(() => userAttempts && userAttempts.filter((a) =>
    (decisionFilter === "all" || (a.admin_decision?.status || "pending") === decisionFilter) &&
    (b1Filter === "all" || (b1Filter === "b1") === isB1(a.total_score)),
  ), [userAttempts, decisionFilter, b1Filter]);

  const adminIds = useMemo(() => new Set((users || []).filter((u) => u.role === "admin").map((u) => u.id)), [users]);

  useEffect(() => { setSelAttempts(new Set()); }, [decisionFilter, b1Filter, selectedUser]);

  const summary = useMemo(() => {
    const admins = adminIds;
    const mine = (attempts || []).filter((a) => !admins.has(a.user_id));
    const byUser = {};
    mine.forEach((a) => { (byUser[a.user_id] ||= []).push(a); });
    const people = Object.values(byUser);
    return {
      tests: mine.length,
      applicants: people.length,
      b1: people.filter((l) => l.some((a) => isB1(a.total_score))).length,
      accepted: people.filter((l) => l.some((a) => a.admin_decision?.status === "accepted")).length,
      pending: people.filter((l) => !l.some((a) => ["accepted", "rejected"].includes(a.admin_decision?.status))).length,
    };
  }, [adminIds, attempts]);

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 px-2">
          <div>
            <div className="overline">Admin</div>
            <h1 className="text-4xl md:text-5xl mt-3 text-foreground font-bold tracking-tight" data-testid="admin-title">
              All users & their <span className="font-display italic text-indigo font-medium">results</span>.
            </h1>
          </div>
          <button
            onClick={exportXlsx}
            disabled={exporting}
            data-testid="admin-export-btn"
            className="neu-btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm"
          >
            <DownloadSimple size={16} weight="bold" /> {exporting ? "Preparing…" : "Export to Excel"}
          </button>
        </div>

        <div className="flex flex-wrap gap-3 px-2" role="tablist">
          {[["users", "Users & results", UsersThree], ["audio", "Audio", SpeakerHigh], ["speaking", "Speaking", Microphone]].map(([id, label, Icon]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 border ${tab === id ? "bg-foreground text-background border-foreground" : "neu-btn"}`}
            >
              <Icon size={16} weight="duotone" /> {label}
            </button>
          ))}
        </div>

        {tab === "audio" ? <AudioManager /> : tab === "speaking" ? <SpeakingManager /> : (<>
        {/* Stats row */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-summary">
          <StatCard icon={ChartBar} label="Tests taken" value={summary.tests} hint={`by ${summary.applicants} applicant${summary.applicants === 1 ? "" : "s"}`} testId="admin-stat-tests" />
          <StatCard icon={Trophy} label="B1 reached" value={summary.b1} hint={`of ${summary.applicants} applicants`} testId="admin-stat-b1" />
          <StatCard icon={CheckCircle} label="Accepted" value={summary.accepted} hint={`of ${summary.applicants} applicants`} testId="admin-stat-accepted" />
          <StatCard icon={Hourglass} label="Awaiting decision" value={summary.pending} hint="applicants" testId="admin-stat-pending" />
        </div>
        <p className="mt-2 px-2 text-xs text-muted-foreground">Counted per applicant (admin accounts excluded). B1 = best score of 70 or more.</p>

        {/* Users */}
        <div className="mt-8 neu rounded-3xl p-6 md:p-8">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <div>
              <div className="overline flex items-center gap-2">
                <UsersThree size={12} weight="duotone" /> Users
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Click a row to filter attempts below by that user.
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
            {selUsers.size > 0 && (
              <button
                onClick={() => askDeleteUsers(users.filter((u) => selUsers.has(u.id)))}
                data-testid="admin-users-bulk-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold bg-red-600 text-white border border-red-600 hover:bg-red-700"
              >
                <Trash size={14} weight="bold" /> Delete selected ({selUsers.size})
              </button>
            )}
            <div className="neu-inset flex items-center gap-2 px-4 py-2 rounded-full w-full sm:w-72">
              <MagnifyingGlass size={14} weight="duotone" className="text-muted-foreground shrink-0" />
              <input
                data-testid="admin-users-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, WhatsApp"
                className="bg-transparent text-sm w-full outline-none"
              />
            </div>
            </div>
          </div>

          {selectedUser && (
            <div className="mb-4 flex items-center gap-3 text-sm neu-inset-sm rounded-full px-4 py-2 w-fit" data-testid="admin-user-filter">
              Filtering attempts by <span className="font-mono-fig text-indigo">{selectedUser.email}</span>
              <button
                onClick={() => setSelectedUser(null)}
                data-testid="admin-user-filter-clear"
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                clear
              </button>
            </div>
          )}

          {users === null ? (
            <div className="text-sm text-muted-foreground font-mono-fig">Loading…</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No users found.</div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0" data-testid="admin-users-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-muted-foreground overline">
                    <th className="px-3 py-2 font-normal w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all users"
                        data-testid="admin-users-select-all"
                        className="accent-[#ff8000] w-4 h-4"
                        checked={selectableUsers.length > 0 && selectableUsers.every((u) => selUsers.has(u.id))}
                        onChange={(e) => setSelUsers(e.target.checked ? new Set(selectableUsers.map((u) => u.id)) : new Set())}
                      />
                    </th>
                    <th className="px-3 py-2 font-normal">Name</th>
                    <th className="px-3 py-2 font-normal">Email</th>
                    <th className="px-3 py-2 font-normal">WhatsApp</th>
                    <th className="px-3 py-2 font-normal text-right">Attempts</th>
                    <th className="px-3 py-2 font-normal">Latest</th>
                    <th className="px-3 py-2 font-normal">Best</th>
                    <th className="px-3 py-2 font-normal">Joined</th>
                    <th className="px-3 py-2 font-normal" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const active = selectedUser?.id === u.id;
                    return (
                      <tr
                        key={u.id}
                        data-testid={`admin-user-row-${u.id}`}
                        onClick={() => setSelectedUser(active ? null : u)}
                        className={`cursor-pointer transition-colors ${active ? "bg-indigo/10" : "hover:bg-foreground/[0.03]"}`}
                      >
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${u.email}`}
                            data-testid={`admin-user-select-${u.id}`}
                            className="accent-[#ff8000] w-4 h-4 disabled:opacity-30"
                            disabled={u.id === me?.id}
                            checked={selUsers.has(u.id)}
                            onChange={() => toggleUser(u.id)}
                          />
                        </td>
                        <td className="px-3 py-3 text-foreground">
                          {u.name || "—"}
                          {u.role === "admin" && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest text-indigo font-mono-fig">admin</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-foreground/85 font-mono-fig text-xs">
                          <span className="inline-flex items-center gap-1.5">
                            <Envelope size={12} weight="duotone" className="text-muted-foreground" />
                            {u.email}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-foreground/85 font-mono-fig text-xs">
                          {u.whatsapp_number ? (
                            <a
                              href={buildWaLink(u.whatsapp_number, u.name)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`admin-wa-link-${u.id}`}
                              title={`WhatsApp ${u.name || u.email}`}
                              className="inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-500 hover:underline transition-colors"
                            >
                              <WhatsappLogo size={14} weight="duotone" />
                              {u.whatsapp_number}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono-fig">{u.attempts_count}</td>
                        <td className="px-3 py-3">
                          {u.latest_attempt ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono-fig">{Math.round(u.latest_attempt.total_score)}</span>
                              <CefrBadge level={u.latest_attempt.cefr_level} size="sm" muted />
                            </div>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-3 py-3">
                          {u.best_attempt ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono-fig">{Math.round(u.best_attempt.total_score)}</span>
                              <CefrBadge level={u.best_attempt.cefr_level} size="sm" muted />
                            </div>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-3 py-3 text-xs text-muted-foreground">
                          {u.created_at ? formatDateTime(u.created_at) : "—"}
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          {u.id !== me?.id && !(u.role === "admin" && u.builtin_admin) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); askRole(u); }}
                              data-testid={`admin-user-role-${u.id}`}
                              title={u.role === "admin" ? "Remove admin access" : "Give admin access"}
                              className={`mr-1 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border ${u.role === "admin" ? "border-foreground/30 text-muted-foreground hover:border-red-600 hover:text-red-600" : "border-foreground hover:bg-foreground hover:text-background"}`}
                            >
                              <ShieldCheck size={13} weight="bold" /> {u.role === "admin" ? "Remove admin" : "Make admin"}
                            </button>
                          )}
                          {u.role === "admin" && u.builtin_admin && u.id !== me?.id && (
                            <span className="mr-2 text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`admin-user-builtin-${u.id}`} title="Set in the app settings; can't be removed here">built-in</span>
                          )}
                          {u.id !== me?.id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); askDeleteUsers([u]); }}
                              data-testid={`admin-user-delete-${u.id}`}
                              aria-label={`Delete ${u.email}`}
                              title="Delete user and their results"
                              className="p-2 border border-transparent hover:border-red-600 hover:text-red-600 text-muted-foreground"
                            >
                              <Trash size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* All attempts */}
        <div className="mt-6 neu rounded-3xl p-6 md:p-8">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <div className="overline flex items-center gap-2">
              <ChartBar size={12} weight="duotone" /> {selectedUser ? "Attempts by user" : "All attempts"}
            </div>
            {selAttempts.size > 0 && (
              <button
                onClick={() => askDeleteAttempts(attempts.filter((a) => selAttempts.has(a.attempt_id)))}
                data-testid="admin-attempts-bulk-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold bg-red-600 text-white border border-red-600 hover:bg-red-700"
              >
                <Trash size={14} weight="bold" /> Delete selected ({selAttempts.size})
              </button>
            )}
          </div>

          {attempts !== null && (
            <AttemptFilters
              decision={decisionFilter} b1={b1Filter} onDecision={setDecisionFilter} onB1={setB1Filter}
              shown={filteredAttempts.length} total={userAttempts.length}
            />
          )}

          {attempts === null ? (
            <div className="text-sm text-muted-foreground font-mono-fig">Loading…</div>
          ) : filteredAttempts.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="admin-attempts-empty">
              {userAttempts.length ? "No results match these filters." : "No attempts yet."}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0" data-testid="admin-attempts-table">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="text-left text-muted-foreground overline">
                    <th className="px-3 py-2 font-normal w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all results"
                        data-testid="admin-attempts-select-all"
                        className="accent-[#ff8000] w-4 h-4"
                        checked={filteredAttempts.length > 0 && filteredAttempts.every((a) => selAttempts.has(a.attempt_id))}
                        onChange={(e) => setSelAttempts(e.target.checked ? new Set(filteredAttempts.map((a) => a.attempt_id)) : new Set())}
                      />
                    </th>
                    <th className="px-3 py-2 font-normal">User</th>
                    <th className="px-3 py-2 font-normal">Email</th>
                    <th className="px-3 py-2 font-normal text-right">Score</th>
                    <th className="px-3 py-2 font-normal">Level</th>
                    <th className="px-3 py-2 font-normal">B1</th>
                    <th className="px-3 py-2 font-normal">Decision</th>
                    <th className="px-3 py-2 font-normal">Duration</th>
                    <th className="px-3 py-2 font-normal">Date</th>
                    <th className="px-3 py-2 font-normal" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredAttempts.map((a) => (
                    <tr key={a.attempt_id} data-testid={`admin-attempt-row-${a.attempt_id}`} className="hover:bg-foreground/[0.03]">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          aria-label={`Select result ${a.attempt_id}`}
                          data-testid={`admin-attempt-select-${a.attempt_id}`}
                          className="accent-[#ff8000] w-4 h-4"
                          checked={selAttempts.has(a.attempt_id)}
                          onChange={() => toggleAttempt(a.attempt_id)}
                        />
                      </td>
                      <td className="px-3 py-3 text-foreground">
                        {a.user_name || "—"}
                        {adminIds.has(a.user_id) && <span className="ml-2 overline !text-[10px] border border-foreground/30 px-1" data-testid={`admin-attempt-admin-tag-${a.attempt_id}`}>admin</span>}
                      </td>
                      <td className="px-3 py-3 text-foreground/85 font-mono-fig text-xs">{a.user_email}</td>
                      <td className="px-3 py-3 text-right font-mono-fig">
                        {Math.round(a.total_score)} <span className="text-muted-foreground text-xs">/ {a.total_max}</span>
                      </td>
                      <td className="px-3 py-3">
                        <CefrBadge level={a.cefr_level} size="sm" muted />
                      </td>
                      <td className="px-3 py-3" data-testid={`admin-attempt-b1-${a.attempt_id}`}>
                        <B1Verdict score={a.total_score} size="sm" />
                      </td>
                      <td className="px-3 py-3" data-testid={`admin-attempt-decision-${a.attempt_id}`}>
                        <DecisionTag decision={a.admin_decision} />
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground font-mono-fig">
                        {formatDuration(a.duration_seconds)}
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{formatDateTime(a.created_at)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <Link
                          to={`/admin/attempts/${a.attempt_id}`}
                          data-testid={`admin-attempt-view-${a.attempt_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border border-foreground hover:bg-foreground hover:text-background mr-1"
                        >
                          <Eye size={13} weight="bold" /> View
                        </Link>
                        <button
                          onClick={() => askDeleteAttempts([a])}
                          data-testid={`admin-attempt-delete-${a.attempt_id}`}
                          aria-label="Delete result"
                          title="Delete this result"
                          className="p-2 border border-transparent hover:border-red-600 hover:text-red-600 text-muted-foreground"
                        >
                          <Trash size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>)}
      </div>
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={runDelete}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="neu rounded-3xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon size={16} weight="duotone" className="text-indigo" />
      </div>
      <div className="font-mono-fig text-3xl text-foreground mt-3" data-testid={`${testId}-value`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

/**
 * Build a wa.me deep-link with a friendly follow-up message.
 * Strips everything except digits (wa.me does NOT want the leading '+').
 */
function buildWaLink(number, name) {
  const cleaned = (number || "").replace(/[^0-9]/g, "");
  const firstName = (name || "").split(" ")[0] || "there";
  const msg =
    `Hi ${firstName}! This is Voxlingua reaching out about your recent ` +
    `English self-assessment. Do you have a moment to chat about your progress?`;
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(msg)}`;
}
